<template>
  <div class="add-order-item-widget">
    <div
      v-if="!hideHeader"
      class="d-flex flex-row align-center order-item flex-nowrap"
    >
      <v-col cols="auto" class="pa-0" v-if="item.picture">
        <v-img class="picture" :src="`${uploadsPath}/${item.picture}`"></v-img>
      </v-col>
      <v-col class="meta flex-fill">
        <h4 class="name">{{ item.name }}</h4>
      </v-col>
    </div>

    <v-skeleton-loader type="paragraph" v-if="!orderItem || loadingOptions" />
    <div class="options" v-else>
      <item-option
        :currency="currency"
        :readonly="false"
        :settings="restaurantSettings"
        :restaurant="restaurant"
        :hide-prices="hidePrices"
        ref="optEls"
        v-for="option of [priceVariants]"
        :item="item"
        :is-editing="!!edit"
        :key="option.id"
        @change="onOptionChange(option, $event)"
        v-if="option"
        :option="option"
      />
      <item-option
        :currency="currency"
        :readonly="false"
        :settings="restaurantSettings"
        :restaurant="restaurant"
        :hide-prices="hidePrices"
        ref="optEls"
        :item="item"
        v-for="option of options"
        v-if="option.active"
        :key="option.id"
        @change="onOptionChange(option, $event)"
        :option="option"
      />
      <item-option
        :currency="currency"
        :readonly="false"
        :restaurant="restaurant"
        :settings="restaurantSettings"
        :hide-prices="hidePrices"
        ref="optEls"
        :item="item"
        v-for="option of additionals"
        v-if="option.active"
        :key="option.id"
        @change="onOptionChange(option, $event)"
        :option="option"
      />
      <div class="item-option">
        <h4 class="option-title font-weight-bold">Comentarios</h4>
        <v-textarea
          placeholder="Comentarios opcionales sobre el producto"
          outlined
          dense
          hide-details
          :rows="2"
          background-color="#ffffff"
          v-model="orderItem.comment"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import { ICurrency } from '../../interfaces/ICurrency';
  import { IRestaurantSettings } from '../../api/services/restaurants.service';
  import { IItem } from '../../interfaces/IItem';
  import { IOption } from '../../interfaces/IOption';
  import { OptionType } from '../../enums/OptionType';
  import ItemsService from '../../api/services/items.service';
  import { vueData } from '../../VueData';
  import ItemOption from './ItemOption.vue';
  import { IOrderItemOption } from '../../interfaces/IOrderItemOption';
  import { IOptionItem } from '../../interfaces/IOptionItem';
  import { TaxDisplayMode } from '../../enums/TaxDisplayMode';
  import { notNull, roundPrice } from '../../utils';
  import { IOrderItem } from '../../interfaces/IOrderItem';
  import { Globals } from '../../modules/Globals';
  import VueNumericInput from 'vue-numeric-input';
  import _ from 'lodash';

  @Component({
    components: { ItemOption, VueNumericInput },
  })
  export default class AddItemWidget extends Vue {
    $refs: {
      optEls: ItemOption[];
    };
    @Prop() restaurant: IRestaurant;
    @Prop() currency: ICurrency;
    @Prop() restaurantSettings: IRestaurantSettings;
    @Prop() item: IItem;
    @Prop() finalOptions?: IOption[];
    @Prop() edit: IOrderItem;
    @Prop({ default: false }) hideHeader: boolean;
    @Prop({ default: false }) hidePrices: boolean;
    uploadsPath = Globals.uploadsPath;
    itemsService = new ItemsService();
    priceVariants: IOption = null;
    allOptions: IOption[] = [];
    loadingOptions: boolean = false;
    orderItem: IOrderItem | null = null;

    async created() {
      this.initOrderItem();

      if (this.item) await this.loadOptions();
    }

    private initOrderItem() {
      if (!this.edit) {
        this.orderItem = {
          amount: 1,
          itemId: this.item.id,
          item: this.item,
          price: this.item.discountedPrice ?? this.item.price,
          comment: '',
          total: this.item.discountedPrice ?? this.item.price,
          options: [],
        };
      } else {
        this.orderItem = _.cloneDeep(this.edit);
      }

      this.$emit('change', this.orderItem);
    }

    mounted() {}

    @Watch('item')
    onItemChange() {
      if (this.item) this.loadOptions();
    }

    get hasOptions() {
      return (
        this.priceVariants || this.options.length || this.additionals.length
      );
    }

    async loadOptions() {
      this.loadingOptions = true;

      try {
        this.priceVariants = this.item.priceVariants;
        if (this.priceVariants) this.allOptions.push(this.priceVariants);

        const options =
          this.finalOptions ??
          (await ItemsService.getFinalOptions(this.item.id));

        this.allOptions.push(...options);

        Vue.nextTick(() => {
          if (!this.$refs.optEls) return;

          const opts = Array.isArray(this.$refs.optEls)
            ? this.$refs.optEls
            : [this.$refs.optEls];

          for (const optEl of opts) {
            const oIO = this.orderItem.options.filter(
              (o) => o.optionItem.optionId === optEl.option.id
            );
            optEl.initFromOrderItemOptions(oIO);
          }
        });
      } catch (e) {
        console.error(e);
        vueData.serverError(e);
      } finally {
        this.loadingOptions = false;
      }
    }

    get options() {
      return this.allOptions.filter(
        (o) => !o.main && o.type === OptionType.Choose
      );
    }

    get additionals() {
      return this.allOptions.filter((o) => o.type === OptionType.Add);
    }

    @Watch('orderItem.amount', { immediate: true })
    onAmountChanged() {
      if (!this.orderItem) return;
      this.orderItem.price = this.unitPrice;
      this.orderItem.total = this.totalPrice;
      this.$emit('change', this.orderItem);
    }

    onOptionChange(option: IOption, changes: { [itemId: number]: number }) {
      for (let i = this.orderItem.options.length; --i >= 0; ) {
        const item = this.orderItem.options[i];
        if (item.optionItem.optionId === option.id)
          this.orderItem.options.splice(i, 1);
      }

      for (const itemId in changes) {
        const val = changes[itemId];
        if (!val) continue;

        const optionItem = this.optionItemsById[itemId];

        let cost = this.applyTax(
          notNull(optionItem.discountedCost, optionItem.cost)
        );

        this.orderItem.options.push({
          optionItem,
          optionItemId: optionItem.id,
          price: cost,
          val,
          itemId: +itemId,
          total: cost * val,
        });
      }

      this.orderItem.price = this.unitPrice;
      this.orderItem.total = this.totalPrice;

      // sort by options.index
      this.orderItem.options.sort((a, b) => {
        const aIndex = a.optionItem.option.id;
        const bIndex = b.optionItem.option.id;
        if (aIndex < bIndex) return -1;
        if (aIndex > bIndex) return 1;
        return 0;
      });

      this.$emit('change', this.orderItem);
    }

    get optionItemsById() {
      const byIds: { [id: number]: IOptionItem } = {};

      for (const option of this.allOptions) {
        for (const item of option.optionItems) {
          if (!item.option) item.option = option;
          byIds[item.id] = item;
        }
      }

      return byIds;
    }

    get orderItemOptionsById() {
      const byIds: { [id: number]: IOrderItemOption } = {};

      if (this.orderItem.options) {
        for (const option of this.orderItem.options) {
          byIds[option.itemId] = option;
        }
      }

      return byIds;
    }

    get hasTax() {
      return (
        this.restaurantSettings.taxMode !== TaxDisplayMode.NoTax &&
        this.restaurant.tax
      );
    }

    applyTax(val: number) {
      if (
        this.hasTax &&
        (this.restaurantSettings.taxMode === TaxDisplayMode.AddAndShow ||
          this.restaurantSettings.taxMode === TaxDisplayMode.SilentAdd)
      ) {
        val *= 1 + this.restaurant.tax / 100;
      }
      return roundPrice(val);
    }

    get unitPrice() {
      if (!this.orderItem) return 0;

      let base = this.applyTax(
        notNull(this.item.discountedPrice, this.item.price)
      );

      let others = 0;

      for (const option of this.orderItem.options) {
        if (option.optionItem.option.main) base = option.total;
        else {
          let price = option.total;
          others += price;
        }
      }

      let total = roundPrice(base + others, true);

      return total;
    }

    get totalPrice() {
      return roundPrice(this.unitPrice * this.orderItem.amount, true);
    }

    // check if all mandatory options are selected. In case of additionals with a minimum, check if the minimum is reached
    get mandatoryOptionsSelected() {
      for (const option of this.options) {
        if (!option.active) continue;

        if (option.min > 0) {
          const sumSelected = this.orderItem.options
            .filter((o) => o.optionItem.optionId === option.id)
            .reduce((sum, o) => sum + o.val, 0);
          if (sumSelected < option.min) return false;
        } else if (option.mandatory) {
          const selected = this.orderItem.options.filter(
            (o) => o.optionItem.optionId === option.id
          );
          if (selected.length === 0) return false;
        }
      }

      if (this.priceVariants) {
        const selected = this.orderItem.options.filter(
          (o) => o.optionItem.optionId === this.priceVariants.id
        );
        if (selected.length === 0) return false;
      }

      for (const option of this.additionals) {
        if (!option.active) continue;

        if (option.min > 0) {
          const sumSelected = this.orderItem.options
            .filter((o) => o.optionItem.optionId === option.id)
            .reduce((sum, o) => sum + o.val, 0);
          if (sumSelected < option.min) return false;
        }
      }

      return true;
    }

    @Watch('mandatoryOptionsSelected', { immediate: true })
    onMandatoryOptionsSelectedChanged(value: boolean) {
      this.$emit('on-validation', value);
    }
  }
</script>
